import { z } from "zod";
import { useAppForm } from "@/shared/forms/app-form";
import { makeClinicalValueFormSchema } from "../../forms/clinical-definition-form";
import type { FindingDefinitionDocument } from "./finding-document";

export const findingDefinitionDocumentSchema = z
  .object({
    kind: z.enum(["Choice", "Measured"]),
    components: z.array(
      z.object({
        id: z.string().optional(),
        key: z.string(),
        label: z.string(),
        value: makeClinicalValueFormSchema(false),
      }),
    ),
    cardinality: z.enum(["Single", "Multiple"]),
    offerOther: z.boolean(),
    options: z.array(
      z.object({
        id: z.string().optional(),
        label: z.string(),
        detail: z.string().optional(),
        normal: z.boolean().optional(),
      }),
    ),
  })
  .superRefine((value, context) => {
    if (value.kind === "Measured") {
      if (!value.components.length)
        context.addIssue({ code: "custom", path: ["components"], message: "At least one component is required" });
      const labels = new Set<string>();
      value.components.forEach((component, index) => {
        const label = component.label.trim();
        if (label && labels.has(label))
          context.addIssue({
            code: "custom",
            path: ["components", index, "label"],
            message: "Component labels must be unique",
          });
        if (label) labels.add(label);
        if (!component.label.trim())
          context.addIssue({
            code: "custom",
            path: ["components", index, "label"],
            message: "Component label is required",
          });
      });
    } else if (!value.options.length || value.options.some((option) => !option.label.trim()))
      context.addIssue({ code: "custom", path: ["options"], message: "Labeled answer options are required" });
  });

export function useFindingDefinitionForm(defaultValues: FindingDefinitionDocument) {
  return useAppForm({
    defaultValues,
    validators: { onDynamic: findingDefinitionDocumentSchema },
    onSubmit: () => {},
  });
}

export type FindingDefinitionForm = ReturnType<typeof useFindingDefinitionForm>;
